import { differenceInCalendarDays, format, subDays } from 'date-fns';
import { FormatMandatoryActivitiesEntry } from '../../interfaces/meeting/mandatory-format-activities.interface';
import {
  ExpenseMasterData,
  ExpenseOption,
  ExpenseStatus,
  MeetingExpense,
} from '../../classes/meeting-expense/meeting-expense.class';
import { NotesAttachmentsConfig } from '../../utility/notes-attachments-config.util';
import { MEETING_EXPENSE_MAX_AMOUNT } from '../../classes/meeting-expense/meeting-expense.constants';

export interface ExpenseSectionContext {
  readOnly: boolean;
  isCompleted: boolean;
  isCanceled: boolean;
  meetingStart: Date | null;
  gracePeriodDays: number;
  today: Date;
}

export interface ExpenseDateBounds {
  min: string;
  max: string;
}

export interface ExpenseFieldErrors {
  date?: string;
  type?: string;
  subtype?: string;
  amount?: string;
}

const REQUIRED = 'THIS_IS_A_REQUIRED_FIELD';
export const INVALID_AMOUNT = 'MEETING_EXPENSE_AMOUNT_INVALID';

export const isExpenseAmountOutOfRange = (amount: number | null | undefined): boolean =>
  amount != null && (amount < 0 || amount > MEETING_EXPENSE_MAX_AMOUNT);

// date-fns 1.x tokens: YYYY/DD (yyyy/dd are not tokens in v1).
export const toDateOnly = (date: Date): string => format(date, 'YYYY-MM-DD');

// new Date('YYYY-MM-DD') parses as UTC midnight, which is the previous day west of UTC.
export function fromDateOnly(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function gracePeriodDaysFromEntry(entry: FormatMandatoryActivitiesEntry | null): number {
  const days = Number(entry?.expenseGracePeriodDays);
  return Number.isFinite(days) && days > 0 ? Math.floor(days) : 0;
}

export function isWithinGracePeriod(ctx: ExpenseSectionContext): boolean {
  if (!ctx.isCompleted) return true;
  if (!ctx.meetingStart) return false;
  return differenceInCalendarDays(ctx.today, ctx.meetingStart) <= ctx.gracePeriodDays;
}

export function canAddExpense(ctx: ExpenseSectionContext): boolean {
  return !ctx.readOnly && !ctx.isCanceled && isWithinGracePeriod(ctx);
}

export function canEditExpense(ctx: ExpenseSectionContext, status: ExpenseStatus): boolean {
  return status === ExpenseStatus.Draft && canAddExpense(ctx);
}

export interface ExpenseAmountFormat {
  symbol: string;
  symbolOnLeft: boolean;
  decimals: number;
}

export function formatExpenseAmount(amount: number | null | undefined, format: ExpenseAmountFormat): string {
  if (amount == null) return '';
  const [integer, fraction] = Number(amount).toFixed(format.decimals).split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+$)/g, ',') + (fraction ? `.${fraction}` : '');
  return format.symbolOnLeft ? `${format.symbol}${grouped}` : `${grouped}${format.symbol}`;
}

export const stepExpenseAmount = (text: string, delta: number): number =>
  Math.max(0, Math.round((Number(text) + delta) * 100) / 100);

export function filterByWords<T extends { title: string }>(items: T[], query: string): T[] {
  const words = query.toLowerCase().split(' ').filter((word) => word.trim() !== '');
  return items.filter((item) => words.every((word) => item.title.toLowerCase().includes(word)));
}

export const shouldBlockCustomerSearch = (readOnly: boolean, meetingContactCount: number, selectedContactId: string | null): boolean =>
  !readOnly && meetingContactCount === 0 && !selectedContactId;

export function expenseDateBounds(today: Date, maxBackDays: number): ExpenseDateBounds {
  return { min: toDateOnly(subDays(today, maxBackDays)), max: toDateOnly(today) };
}

export function defaultExpenseDate(meetingStart: Date | null, today: Date, maxBackDays: number): string {
  const bounds = expenseDateBounds(today, maxBackDays);
  const start = meetingStart ? toDateOnly(meetingStart) : bounds.max;
  if (start > bounds.max) return bounds.max;
  if (start < bounds.min) return bounds.min;
  return start;
}

export function subtypesForType(master: ExpenseMasterData, typeId: string | null): ExpenseOption[] {
  if (!typeId) return [];
  const allowed = new Set(master.pairs.filter((pair) => pair.typeId === typeId).map((pair) => pair.subtypeId));
  return master.subtypes.filter((subtype) => allowed.has(subtype.id));
}

export function isSubtypeRequired(master: ExpenseMasterData, typeId: string | null): boolean {
  return subtypesForType(master, typeId).length > 0;
}

// Only the future is rejected here: the back-dating limit is enforced by the picker, so a saved date stays submittable as the window slides.
export function validateExpense(expense: MeetingExpense, master: ExpenseMasterData, today: Date = new Date()): ExpenseFieldErrors {
  const errors: ExpenseFieldErrors = {};
  if (!expense.expenseDate) errors.date = REQUIRED;
  else if (expense.expenseDate.slice(0, 10) > toDateOnly(today)) errors.date = 'MEETING_EXPENSE_DATE_IN_FUTURE';
  if (!expense.typeId) errors.type = REQUIRED;
  if (isSubtypeRequired(master, expense.typeId)) {
    const paired = subtypesForType(master, expense.typeId).some((subtype) => subtype.id === expense.subtypeId);
    if (!paired) errors.subtype = REQUIRED;
  }
  if (isExpenseAmountOutOfRange(expense.amount)) errors.amount = INVALID_AMOUNT;
  else if (!(Number(expense.amount) > 0)) errors.amount = REQUIRED;
  return errors;
}

export function isExpenseComplete(expense: MeetingExpense, master: ExpenseMasterData): boolean {
  return Object.keys(validateExpense(expense, master)).length === 0;
}

// Shared by manual Submit and the completion sweep, so a Draft the sweep submits is one a rep could have submitted.
export function canSubmitExpense(
  expense: MeetingExpense,
  master: ExpenseMasterData,
  notesConfig: NotesAttachmentsConfig,
  today: Date = new Date(),
): boolean {
  if (Object.keys(validateExpense(expense, master, today)).length > 0) return false;
  if (notesConfig.notesMandatory && !expense.notes.some((note) => (note.noteText ?? '').trim().length > 0)) return false;
  if (notesConfig.attachmentsMandatory && !expense.notes.some((note) => note.hasDocument)) return false;
  return true;
}

export type FormatChangeExpenseAction = 'none' | 'block' | 'confirmScrap';

export function formatChangeExpenseAction(
  newFormatShowsExpenses: boolean,
  hasNonDraft: boolean,
  hasDraft: boolean,
): FormatChangeExpenseAction {
  if (newFormatShowsExpenses) return 'none';
  if (hasNonDraft) return 'block';
  return hasDraft ? 'confirmScrap' : 'none';
}
